// Pestaña "Directivas y cuaderno" de la pantalla del asistente: a la izquierda las directivas del
// dueño (órdenes que el asistente debe seguir) y a la derecha el cuaderno del asistente (lo que va
// aprendiendo). Cada sección tiene formulario para agregar, lista con edición en el lugar y
// desactivar / reactivar. Arriba (solo el Administrador): las reglas que el cerebro propone; abajo,
// el cuaderno de reglas vigente del ayudante de preguntas (cerebro, etapa 3).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Loader2, Pencil, Power, ScrollText, NotebookPen } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';
import { useAuthStore } from '../../stores/authStore';
import { fmtDateTime, sourceLabel, useSeesAllSellers, type DirectiveRow, type NotebookRow } from './shared';
import RulesProposed, { FaqCurrent, type RulesData } from './RulesProposed';
import type { FaqItem } from './rulesView';

const NOTEBOOK_CATEGORIES = [
  { key: 'estrategia', label: 'Estrategia' },
  { key: 'correccion', label: 'Corrección' },
  { key: 'caso', label: 'Caso' },
  { key: 'contexto', label: 'Contexto' },
] as const;
const categoryLabel = (k: string): string => NOTEBOOK_CATEGORIES.find((c) => c.key === k)?.label ?? k;

const MAX_TEXT = 4000;

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const btnPrimary =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';
const btnGhost =
  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-40';

type Kind = 'directives' | 'notebook';
type Entry = (DirectiveRow | NotebookRow) & { category?: string; author?: string; scope?: string };
type Toast = { kind: 'ok' | 'err'; msg: string } | null;

function Section({
  kind,
  title,
  hint,
  icon,
  reloadKey = 0,
}: {
  kind: Kind;
  title: string;
  hint: string;
  icon: React.ReactNode;
  /** Al cambiar, se vuelve a leer (p. ej. se aprobó una regla que crea una directiva). */
  reloadKey?: number;
}) {
  const sellerId = useUIStore((s) => s.sellerId);
  const sellers = useUIStore((s) => s.sellers);
  const seesAll = useSeesAllSellers();
  const isNotebook = kind === 'notebook';
  const noun = isNotebook ? 'nota' : 'directiva';

  const [rows, setRows] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqSeq = useRef(0);

  // Formulario de alta
  const [text, setText] = useState('');
  const [scope, setScope] = useState<'global' | 'seller'>('seller');
  const [category, setCategory] = useState<string>('estrategia');
  const [saving, setSaving] = useState(false);

  // Edición en el lugar
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [busy, setBusy] = useState<Record<number, boolean>>({});

  const showToast = (t: Toast) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    const failMsg = isNotebook ? 'No se pudo cargar el cuaderno.' : 'No se pudieron cargar las directivas.';
    try {
      const qs = new URLSearchParams({ seller_id: String(sellerId) });
      if (showInactive) qs.set('include_inactive', '1');
      const res = await api.get<Entry[]>(`/assistant/${kind}?${qs.toString()}`);
      if (seq !== reqSeq.current) return;
      if (res.success && res.data) {
        setRows(res.data);
        setError(null);
      } else {
        setError(res.error || failMsg);
      }
    } catch {
      if (seq === reqSeq.current) setError(failMsg);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // reloadKey solo dispara la relectura.
  }, [kind, isNotebook, sellerId, showInactive, reloadKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const body = isNotebook
        ? { category, seller_id: scope === 'global' ? null : sellerId, text: t }
        : scope === 'global'
          ? { scope: 'global', text: t }
          : { scope: 'seller', seller_id: sellerId, text: t };
      const res = await api.post(`/assistant/${kind}`, body);
      if (res.success) {
        setText('');
        showToast({ kind: 'ok', msg: isNotebook ? 'Nota guardada' : 'Directiva guardada' });
        void load();
      } else {
        showToast({ kind: 'err', msg: res.error || `No se pudo guardar la ${noun}.` });
      }
    } catch {
      showToast({ kind: 'err', msg: `No se pudo guardar la ${noun}.` });
    } finally {
      setSaving(false);
    }
  };

  const patch = async (row: Entry, body: Record<string, unknown>, okMsg: string) => {
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      const res = await api.patch(`/assistant/${kind}/${row.id}`, body);
      if (res.success) {
        showToast({ kind: 'ok', msg: okMsg });
        setEditId(null);
        void load();
      } else {
        showToast({ kind: 'err', msg: res.error || `No se pudo actualizar la ${noun}.` });
      }
    } catch {
      showToast({ kind: 'err', msg: `No se pudo actualizar la ${noun}.` });
    } finally {
      setBusy((b) => ({ ...b, [row.id]: false }));
    }
  };

  const startEdit = (row: Entry) => {
    setEditId(row.id);
    setEditText(row.text);
    setEditCategory(row.category ?? 'estrategia');
  };

  const saveEdit = (row: Entry) => {
    const t = editText.trim();
    if (!t) return;
    const body: Record<string, unknown> = {};
    if (t !== row.text) body.text = t;
    if (isNotebook && editCategory !== row.category) body.category = editCategory;
    if (Object.keys(body).length === 0) {
      setEditId(null);
      return;
    }
    void patch(row, body, 'Cambios guardados');
  };

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-semibold text-[var(--color-text)]">
            {icon}
            {title}
          </div>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">{hint}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          title="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] shrink-0"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {toast && (
        <div
          className={`rounded-lg p-3 text-sm ${
            toast.kind === 'ok'
              ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]'
              : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Formulario de alta */}
      <Can cap="assistant.manage">
        <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_TEXT}
            rows={3}
            placeholder={isNotebook ? 'Escribí la nota…' : 'Escribí la directiva…'}
            className={`${inputCls} w-full resize-y`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as 'global' | 'seller')}
              className={inputCls}
              title="A quién aplica"
            >
              <option value="seller">Solo esta empresa ({sellerName(sellerId)})</option>
              {seesAll && <option value="global">Global (todas las empresas)</option>}
            </select>
            {isNotebook && (
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} title="Categoría">
                {NOTEBOOK_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            )}
            <button type="button" onClick={() => void create()} disabled={saving || !text.trim()} className={btnPrimary}>
              {saving && <Loader2 size={12} className="animate-spin" />}
              Guardar
            </button>
          </div>
        </div>
      </Can>

      <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Mostrar inactivas
      </label>

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && !error ? (
        <div className="flex items-center justify-center py-8 text-[var(--color-text-secondary)]">
          <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
        </div>
      ) : rows.length === 0 && !error ? (
        <div className="text-center py-6 text-sm text-[var(--color-text-secondary)]">
          {isNotebook ? 'El cuaderno está vacío.' : 'No hay directivas.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const editing = editId === r.id;
            // Una entrada global solo la puede tocar quien ve todas las empresas.
            // Las copiadas del bot de reclamos no se editan acá: el bot no las lee de esta tabla.
            const fromClaims = r.source === 'claims';
            const writable = (r.seller_id !== null || seesAll) && !fromClaims;
            return (
              <li
                key={r.id}
                className={`rounded-lg border border-[var(--color-border)] p-3 space-y-2 ${
                  r.active ? '' : 'opacity-60'
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)]">
                  <span>{fmtDateTime(r.created_at)}</span>
                  <span>·</span>
                  <span>
                    {sourceLabel(r.source)}
                    {r.author ? ` (${r.author})` : ''}
                  </span>
                  <span>·</span>
                  <span className="font-medium text-[var(--color-text)]">
                    {r.seller_id === null ? 'Global' : sellerName(r.seller_id)}
                  </span>
                  {isNotebook && r.category && (
                    <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]">
                      {categoryLabel(r.category)}
                    </span>
                  )}
                  {!r.active && (
                    <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]">
                      {!isNotebook && r.source === 'agent' ? 'Pendiente de confirmar' : 'Inactiva'}
                    </span>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      maxLength={MAX_TEXT}
                      rows={3}
                      className={`${inputCls} w-full resize-y`}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {isNotebook && (
                        <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className={inputCls}>
                          {NOTEBOOK_CATEGORIES.map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => saveEdit(r)}
                        disabled={!!busy[r.id] || !editText.trim()}
                        className={btnPrimary}
                      >
                        {busy[r.id] && <Loader2 size={12} className="animate-spin" />}
                        Guardar
                      </button>
                      <button type="button" onClick={() => setEditId(null)} className={btnGhost}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-[var(--color-text)] whitespace-pre-wrap break-words">{r.text}</div>
                )}

                {fromClaims && (
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Copia de las reglas del bot de reclamos. Cambiarla acá no cambia al bot.
                  </p>
                )}

                {!editing && writable && (
                  <Can cap="assistant.manage">
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => startEdit(r)} disabled={!!busy[r.id]} className={btnGhost}>
                        <Pencil size={12} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          void patch(
                            r,
                            { active: !r.active },
                            r.active
                              ? isNotebook
                                ? 'Nota desactivada'
                                : 'Directiva desactivada'
                              : isNotebook
                                ? 'Nota reactivada'
                                : 'Directiva reactivada'
                          )
                        }
                        disabled={!!busy[r.id]}
                        className={btnGhost}
                      >
                        {busy[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                        {r.active ? 'Desactivar' : !isNotebook && r.source === 'agent' ? 'Confirmar' : 'Reactivar'}
                      </button>
                    </div>
                  </Can>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function DirectivesTab({ onRulesChanged }: { onRulesChanged?: () => void }) {
  // Las reglas propuestas y el cuaderno vigente solo los puede usar el Administrador (el servidor
  // lo exige igual); a los demás roles no se les muestra la sección.
  const isOwner = useAuthStore((s) => !!s.user?.is_admin);
  const [directivesKey, setDirectivesKey] = useState(0);
  const [rulesNonce, setRulesNonce] = useState(0);
  const [faq, setFaq] = useState<FaqItem[] | null>(null);
  const [faqFailed, setFaqFailed] = useState(false);

  const onData = useCallback((d: RulesData | null, failed: boolean) => {
    setFaq(d ? d.faq : null);
    setFaqFailed(failed);
  }, []);
  const onDecided = useCallback(() => {
    setDirectivesKey((k) => k + 1);
    onRulesChanged?.();
  }, [onRulesChanged]);
  const onStruck = useCallback(() => setRulesNonce((n) => n + 1), []);

  return (
    <div className="space-y-4">
      {isOwner && <RulesProposed reloadNonce={rulesNonce} onData={onData} onDecided={onDecided} />}
      <div className="grid gap-4 xl:grid-cols-2">
        <Section
          kind="directives"
          title="Directivas del dueño"
          hint="Órdenes que el asistente tiene que seguir siempre. Las globales valen para todas las empresas."
          icon={<ScrollText size={16} className="text-[var(--color-primary)]" />}
          reloadKey={directivesKey}
        />
        <Section
          kind="notebook"
          title="Cuaderno del asistente"
          hint="Lo que el asistente va aprendiendo: estrategias, correcciones, casos y contexto."
          icon={<NotebookPen size={16} className="text-[var(--color-primary)]" />}
        />
      </div>
      {isOwner && <FaqCurrent faq={faq} failed={faqFailed} onStruck={onStruck} />}
    </div>
  );
}
